/** Controls reconnecting of the client */
export interface ReconnectPolicy {
  /** Called after disconnect was detected
   *
   * @returns {number | null} Amount of time in milliseconds to delay the next reconnect attempt, `null` signals to stop retrying.
   */
  nextRetryDelayInMs(context: ReconnectContext): number | null;
}

export interface ReconnectContext {
  /**
   * Number of failed reconnect attempts
   */
  readonly retryCount: number;

  /**
   * Elapsed amount of time in milliseconds since the disconnect.
   */
  readonly elapsedMs: number;

  /**
   * Reason for retrying
   */
  readonly retryReason?: Error;

  readonly serverUrl?: string;
}

// ---------------------------------------------------------------------------
// Policy implementations (bridge-restart recovery, engineer repro 2026-08-25).
//
// The two rooms need OPPOSITE give-up behavior:
//
// - The CLOUD room's URL is stable. Retrying it forever is correct — when the
//   WAN returns, the same URL works. Give up = permanently dead transport.
//
// - A LOCAL room's URL embeds the SFU's RANDOM per-boot port. If the bridge
//   (or just iris-local-server) restarts, that URL is dead FOREVER — an
//   infinite retry loop pins the room in Reconnecting, never emits
//   Disconnected, and therefore never triggers the fresh-token rebuild that
//   re-probes the NEW port. This was the "remote studio never got video
//   back" incident. The local policy retries fast (transient blips heal in
//   well under a second on a LAN) and then gives up so the rebuild path
//   takes over.
// ---------------------------------------------------------------------------

/** Local rooms: fast early retries, then hand off to the rebuild path. */
export const LOCAL_RECONNECT_MAX_RETRIES = 6;
export const LOCAL_RECONNECT_MAX_ELAPSED_MS = 15_000;

const LOCAL_RETRY_DELAYS_MS = [0, 250, 500, 1_000, 2_000, 4_000];

export const localReconnectPolicy: ReconnectPolicy = {
  nextRetryDelayInMs: (context: ReconnectContext): number | null => {
    if (
      context.retryCount >= LOCAL_RECONNECT_MAX_RETRIES ||
      context.elapsedMs >= LOCAL_RECONNECT_MAX_ELAPSED_MS
    ) {
      return null; // -> Disconnected -> #rebuildLocalAfterDisconnect + presence recovery
    }
    return LOCAL_RETRY_DELAYS_MS[context.retryCount] ?? 4_000;
  }
};

/** Cloud room: retry forever with a linear-then-capped backoff. */
export const cloudReconnectPolicy: ReconnectPolicy = {
  nextRetryDelayInMs: (context: ReconnectContext): number | null => {
    return Math.min(1000 * 15, context.retryCount * 1000);
  }
};
